import { useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { getSocket } from '../lib/socket';

export interface InAppNotification {
  id: string;
  user_id: string;
  workspace_id: string | null;
  kind: string;
  title: string;
  body: string | null;
  action_url: string | null;
  metadata: Record<string, unknown> | null;
  is_read: boolean;
  read_at: string | null;
  created_at: string;
}

/**
 * Hook for the bell + dropdown. Pulls the latest notifications + unread
 * count, then subscribes to `notification:new` / `notification:read` /
 * `notification:read-all` from the Socket.io gateway so the badge
 * updates in real time. The 60s background poll catches anything the
 * socket missed during a brief disconnect.
 */
export function useNotifications(enabled: boolean = true) {
  const qc = useQueryClient();

  const list = useQuery<InAppNotification[]>({
    queryKey: ['notifications'],
    queryFn: async () => (await api.get('/notifications', { params: { limit: 25 } })).data,
    enabled,
    refetchInterval: 60_000,
  });

  const unread = useQuery<{ count: number }>({
    queryKey: ['notifications', 'unread'],
    queryFn: async () => (await api.get('/notifications/unread-count')).data,
    enabled,
    refetchInterval: 60_000,
  });

  useEffect(() => {
    if (!enabled) return;
    const socket = getSocket();

    function onNew(_n: InAppNotification) {
      void _n;
      qc.invalidateQueries({ queryKey: ['notifications'] });
    }
    function onReadOne(_: { id: string }) {
      qc.invalidateQueries({ queryKey: ['notifications'] });
    }
    function onReadAll(_: { count: number }) {
      qc.invalidateQueries({ queryKey: ['notifications'] });
    }

    socket.on('notification:new', onNew);
    socket.on('notification:read', onReadOne);
    socket.on('notification:read-all', onReadAll);

    return () => {
      socket.off('notification:new', onNew);
      socket.off('notification:read', onReadOne);
      socket.off('notification:read-all', onReadAll);
    };
  }, [qc, enabled]);

  async function markRead(id: string) {
    await api.post(`/notifications/${id}/read`);
    qc.invalidateQueries({ queryKey: ['notifications'] });
  }

  async function markAllRead() {
    await api.post('/notifications/read-all');
    qc.invalidateQueries({ queryKey: ['notifications'] });
  }

  return {
    items: list.data ?? [],
    unreadCount: unread.data?.count ?? 0,
    loading: list.isLoading,
    markRead,
    markAllRead,
  };
}
